import { debugLog } from "./debug";

export interface TaskEmailSnapshot {
  subject: string;
  from: string;
}

export interface FollowUpTask {
  id: string;
  title: string;
  /** ISO date (e.g. "2026-07-10"); optional. */
  dueDate?: string;
  /** Snapshot of the source email at creation; no received date (EmailContext lacks one). */
  email: TaskEmailSnapshot;
  done: boolean;
  /** ms epoch — also serves as the "captured on" date. */
  createdAt: number;
  /** Microsoft To Do task id, once one-way synced (see ChatPanel's trySyncTask).
   * Absent = never synced (local-only account, no consent yet, or a prior sync
   * attempt failed) — never treated as an error state, just "not synced". */
  graphTaskId?: string;
}

export const TASKS_KEY = "aiAssistant.tasks.v1";

/** Reads and parses the stored array; corrupt/absent storage → []. Never throws. */
function readAll(): FollowUpTask[] {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(TASKS_KEY);
  } catch (e) {
    debugLog("tasks:read-failed", e instanceof Error ? e.name : "unknown");
    return [];
  }
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as FollowUpTask[]) : [];
  } catch {
    return [];
  }
}

/** Persists best-effort; a quota/disabled-storage failure is logged, not thrown. */
function writeAll(tasks: FollowUpTask[]): void {
  try {
    localStorage.setItem(TASKS_KEY, JSON.stringify(tasks));
  } catch (e) {
    debugLog("tasks:write-failed", e instanceof Error ? e.name : "unknown");
  }
}

export function newTaskId(): string {
  return crypto.randomUUID();
}

/** All tasks, newest first by createdAt. */
export function loadTasks(): FollowUpTask[] {
  return readAll().sort((a, b) => b.createdAt - a.createdAt);
}

export function createTask(input: {
  title: string;
  dueDate?: string;
  email: TaskEmailSnapshot;
}): FollowUpTask {
  const task: FollowUpTask = {
    id: newTaskId(),
    title: input.title,
    dueDate: input.dueDate,
    email: input.email,
    done: false,
    createdAt: Date.now(),
  };
  writeAll([task, ...readAll()]);
  return task;
}

export function toggleTaskDone(id: string): FollowUpTask[] {
  const next = readAll().map((t) => (t.id === id ? { ...t, done: !t.done } : t));
  writeAll(next);
  return next;
}

export function updateTaskTitle(id: string, title: string): FollowUpTask[] {
  const next = readAll().map((t) => (t.id === id ? { ...t, title } : t));
  writeAll(next);
  return next;
}

export function updateTaskDueDate(id: string, dueDate: string | undefined): FollowUpTask[] {
  const next = readAll().map((t) => (t.id === id ? { ...t, dueDate } : t));
  writeAll(next);
  return next;
}

/** Records (or clears) the Microsoft To Do task id after a sync attempt. */
export function setGraphTaskId(id: string, graphTaskId: string | undefined): FollowUpTask[] {
  const next = readAll().map((t) => (t.id === id ? { ...t, graphTaskId } : t));
  writeAll(next);
  return next;
}

export function deleteTask(id: string): FollowUpTask[] {
  const next = readAll().filter((t) => t.id !== id);
  writeAll(next);
  return next;
}

/** Overdue = a due date set, not done, and strictly before today (date-only). */
export function isOverdue(task: FollowUpTask, now: Date = new Date()): boolean {
  if (!task.dueDate || task.done) return false;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const due = new Date(`${task.dueDate}T00:00:00`);
  return due.getTime() < today.getTime();
}
